import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
    Search,
    ArrowRight,
    Plus,
    Pencil,
    Clapperboard,
    Wand2,
    ChevronDown,
    Layers,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import SegmentedControl from "../components/SegmentedControl";
import AddSceneDialog from "../components/AddSceneDialog";
import LookFilter from "../components/LookFilter";
import EmptyScriptOnboarding from "../components/EmptyScriptOnboarding";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

const LENS_KEY = "onsetflow:scenes-lens";

const LENS_LABELS = {
    prep: "Fittings",
    onset: "On Set",
};

function readLens() {
    try {
        const v = localStorage.getItem(LENS_KEY);
        if (v === "prep" || v === "onset") return v;
    } catch { /* localStorage may be unavailable */ }
    return "onset";
}

function saveLens(v) {
    try {
        localStorage.setItem(LENS_KEY, v);
    } catch { /* ignore — preference is non-essential */ }
}

// Iter-127 — episode grouping is now shared across ScenesHub +
// OnSetMode + PrepMode via lib/episodeGroups so all three surfaces
// render the same accordion UX for episodic projects.
import {
    NO_EPISODE,
    EPISODE_LABEL_FILM,
    EPISODE_LABEL_TV,
    groupScenesByEpisode,
    isTvProject,
} from "../lib/episodeGroups";

/**
 * iter-48: parse the user's search query into a structured filter.
 * Supports three patterns:
 *   "1.4"   → {episode: "1", scene: "4"} (TV combined numbering)
 *   "1·4"   → same (we tolerate the middle dot too)
 *   "ep1"   → {episode: "1"} (filter to episode 1, all scenes)
 *   anything else → {text: q.toLowerCase()} (legacy substring search)
 */
function parseQuery(q) {
    const trimmed = q.trim();
    if (!trimmed) return { text: "" };
    const dotted = trimmed.match(/^(\w+)[.·](\w+)$/);
    if (dotted) {
        return {
            episode: dotted[1].toLowerCase(),
            scene: dotted[2].toUpperCase(),
        };
    }
    const epOnly = trimmed.match(/^ep(?:isode)?[.\s]*(\w+)$/i);
    if (epOnly) {
        return { episode: epOnly[1].toLowerCase() };
    }
    return { text: trimmed.toLowerCase() };
}

export default function ScenesHub() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const routeBase = useRouteBase();
    const apiBase = useApiBase();

    const [project, setProject] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [sceneCharMap, setSceneCharMap] = useState({});
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [lens, setLens] = useState(readLens);
    const [addOpen, setAddOpen] = useState(false);
    const [collapsed, setCollapsed] = useState({}); // ep-key → bool
    // Iter-118.13 PART 1 — Look filter on ScenesHub. When a Look is
    // selected we narrow the scene list down to only scenes that have
    // at least one state_entry matching that look. Server-side
    // (`/scenes-by-look`) so the filter stays accurate even for
    // large projects we don't fully hydrate up-front.
    const [projectLooks, setProjectLooks] = useState([]);
    const [lookFilter, setLookFilter] = useState("");
    const [lookSceneIds, setLookSceneIds] = useState(null); // Set or null when no filter
    const [lookLoading, setLookLoading] = useState(false);
    const searchRef = useRef(null);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Iter-83 / Iter-107 — fine-grained scene caps. UI hides Add Scene when the
    // user lacks `scenes_add`; backend enforces the same gate
    // independently. Admins resolve to all-true via /permissions/me.
    const canAddScenes =
        !isArchived &&
        (isOwner || isAdmin || Boolean(permissions?.scenes_add));

    // Iter-158.13.2.20f.42-final — Bulk Edit Scenes affordance.
    // Mirrors CastHub's `cast-hub-bulk-edit-link` gating pattern:
    // owner OR admin OR `scenes_edit` cap. Visible in BOTH `prep`
    // and `onset` lenses — Bulk Edit Scenes is lens-independent
    // (whole-script editing workspace).
    const canBulkEditScenes =
        !isArchived &&
        (isOwner || isAdmin || Boolean(permissions?.scenes_edit));

    useEffect(() => {
        const ctrl = new AbortController();
        const reload = async (signal) => {
            try {
                const [{ data: p }, { data: sc }, { data: links }, { data: perms }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`, { signal }),
                    api.get(`${apiBase}/${projectId}/scenes`, {
                        // Iter-149.1 (P0) — DO NOT filter by status.
                        // Both "draft" (freshly parsed) and "confirmed"
                        // (manually curated) scenes must be visible
                        // here. The review/confirm step is an
                        // optional curation tool, not a visibility
                        // gate. "Script is God" — every parsed
                        // scene must be reachable directly from
                        // Scenes hub without the user having to
                        // re-confirm them first.
                        signal,
                    }),
                    api.get(`${apiBase}/${projectId}/scene-characters`, { signal }),
                    api.get(`${apiBase}/${projectId}/permissions/me`, { signal }),
                ]);
                setProject(p);
                setPermissions(perms?.permissions || {});
                setScenes(sc);
                const map = {};
                for (const l of links || []) {
                    if (!l?.scene_id || !l?.name) continue;
                    (map[l.scene_id] = map[l.scene_id] || []).push(l.name);
                }
                setSceneCharMap(map);
            } catch (e) {
                if (signal?.aborted) return;
                setError(formatApiError(e));
            } finally {
                if (!signal?.aborted) setLoading(false);
            }
        };
        reload(ctrl.signal);
        // iter-74 PART 2 — refetch on capture-save so the row's
        // shot-dot indicator updates without leaving the hub.
        const onSaved = () => reload(undefined);
        window.addEventListener("onsetflow:state-entry-saved", onSaved);
        return () => {
            ctrl.abort();
            window.removeEventListener("onsetflow:state-entry-saved", onSaved);
        };
    }, [projectId]);

    const parsedQuery = useMemo(() => parseQuery(q), [q]);

    // Iter-118.13 PART 1 — fetch the distinct Look values used in this
    // project (drives the chip rail). Cheap call (cap'd to 24 looks).
    useEffect(() => {
        let cancelled = false;
        api.get(`${apiBase}/${projectId}/looks`)
            .then(({ data }) => {
                if (!cancelled) setProjectLooks(data?.looks || []);
            })
            .catch(() => {
                if (!cancelled) setProjectLooks([]);
            });
        // Refetch on capture-save so a freshly typed look appears in the rail.
        const onSaved = () => {
            api.get(`${apiBase}/${projectId}/looks`)
                .then(({ data }) => setProjectLooks(data?.looks || []))
                .catch(() => {});
        };
        window.addEventListener("onsetflow:state-entry-saved", onSaved);
        return () => {
            cancelled = true;
            window.removeEventListener("onsetflow:state-entry-saved", onSaved);
        };
    }, [projectId]);

    // Iter-118.13 PART 1 — when the user picks a Look, hit the server
    // for the matching scene_ids. Server-side so it covers state_entries
    // that haven't been hydrated client-side. Empty look → clear filter.
    useEffect(() => {
        if (!lookFilter) {
            setLookSceneIds(null);
            return;
        }
        let cancelled = false;
        setLookLoading(true);
        api.get(`${apiBase}/${projectId}/scenes-by-look`, { params: { look: lookFilter } })
            .then(({ data }) => {
                if (cancelled) return;
                setLookSceneIds(new Set(data?.scene_ids || []));
            })
            .catch(() => {
                if (!cancelled) setLookSceneIds(new Set());
            })
            .finally(() => {
                if (!cancelled) setLookLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, lookFilter]);

    // Iter-127 — group via the shared util so OnSetMode + PrepMode
    // produce byte-identical bucket ordering. The local memo keeps the
    // grouping cached against the scene-list reference.
    const grouped = useMemo(() => groupScenesByEpisode(scenes), [scenes]);

    // The presence of any explicit episode flips the UI into "TV mode"
    // (always grouped). Pure-film projects (every scene's episode is null)
    // stay flat — same UX they had before.
    const isTvMode = useMemo(() => isTvProject(grouped), [grouped]);

    const location = useLocation();
    const expandEpisodeFromNav = location.state?.expandEpisode || null;
    const scrollToSceneId = location.state?.scrollToSceneId || null;

    // Iter-155B.1 PART 1 — Episode collapse state is now persisted in
    // localStorage per project so coming back from a scene detail
    // restores the episode the user was browsing. Previously every
    // episode collapsed on mount (iter-102), which dropped people back
    // at "Scenes Overview" and forced them to re-find their episode.
    //
    // Priority order on mount:
    //   1. If nav state carries `expandEpisode` (the back-link from
    //      SceneDetail does this), expand that episode and collapse
    //      the others — the user just came from there.
    //   2. Otherwise, restore from localStorage (last expanded set).
    //   3. Otherwise, fall back to iter-102 "all collapsed".
    useEffect(() => {
        if (!isTvMode || grouped.length === 0) return;
        const storageKey = `onsetflow:scenes-hub:expanded:${projectId}`;
        let stored = null;
        try {
            const raw = window.localStorage.getItem(storageKey);
            stored = raw ? JSON.parse(raw) : null;
        } catch {
            stored = null;
        }
        setCollapsed((prev) => {
            const next = { ...prev };
            grouped.forEach((g) => {
                if (next[g.key] !== undefined) return; // user already toggled this run
                if (expandEpisodeFromNav && g.key === expandEpisodeFromNav) {
                    next[g.key] = false;
                } else if (
                    !expandEpisodeFromNav &&
                    stored &&
                    typeof stored[g.key] === "boolean"
                ) {
                    next[g.key] = stored[g.key];
                } else {
                    next[g.key] = true;
                }
            });
            return next;
        });
        // Restore scroll on the scene that was just opened.
        if (scrollToSceneId) {
            // wait one tick for the expanded episode to render
            const t = setTimeout(() => {
                const el = document.querySelector(
                    `[data-testid="scenes-hub-scene-${scrollToSceneId}"]`,
                );
                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ block: "center", behavior: "auto" });
                }
            }, 50);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [isTvMode, grouped, expandEpisodeFromNav, projectId, scrollToSceneId]);

    // Persist whenever the user changes collapsed state.
    useEffect(() => {
        if (!isTvMode) return;
        if (!projectId) return;
        try {
            window.localStorage.setItem(
                `onsetflow:scenes-hub:expanded:${projectId}`,
                JSON.stringify(collapsed),
            );
        } catch {
            // localStorage may be unavailable in private mode — drop silently.
        }
    }, [collapsed, isTvMode, projectId]);

    // Filter the grouped buckets given the parsed query. We DO NOT filter
    // out empty episodes when the user is searching by episode only — we
    // want them to see "Episode 1: 0 matches" feedback if they typo.
    const filteredGroups = useMemo(() => {
        const hasLook = lookSceneIds instanceof Set;
        if (!parsedQuery.text && !parsedQuery.episode && !parsedQuery.scene && !hasLook) {
            return grouped;
        }
        const matchScene = (s, sceneNum) =>
            (s.scene_number || "").toUpperCase().startsWith(sceneNum);
        return grouped
            .map((g) => {
                const epKey = g.key === NO_EPISODE ? "" : g.key.toLowerCase();
                if (parsedQuery.episode && epKey !== parsedQuery.episode) {
                    return null; // wrong episode
                }
                let next = g.scenes;
                if (hasLook) {
                    next = next.filter((s) => lookSceneIds.has(s.id));
                }
                if (parsedQuery.scene) {
                    next = next.filter((s) => matchScene(s, parsedQuery.scene));
                }
                if (parsedQuery.text) {
                    const t = parsedQuery.text;
                    next = next.filter((s) => {
                        const haystack = [
                            s.scene_number,
                            s.episode,
                            s.heading,
                            s.int_ext,
                            s.time_of_day,
                            ...(sceneCharMap[s.id] || []),
                        ]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase();
                        return haystack.includes(t);
                    });
                }
                return { ...g, scenes: next };
            })
            .filter(Boolean)
            .filter((g) => g.scenes.length > 0);
    }, [grouped, parsedQuery, sceneCharMap, lookSceneIds]);

    // While the user is searching auto-expand every match-bearing group.
    useEffect(() => {
        if (!q.trim()) return;
        setCollapsed((prev) => {
            const next = { ...prev };
            for (const g of filteredGroups) next[g.key] = false;
            return next;
        });
    }, [q, filteredGroups]);

    const switchLens = (next) => {
        setLens(next);
        saveLens(next);
    };

    const toggleEp = (key) =>
        setCollapsed((c) => ({ ...c, [key]: !c[key] }));

    const openFullMode = () => {
        navigate(`${routeBase}/${projectId}/${lens === "prep" ? "prep" : "onset"}`);
    };

    // iter-63 PART 1: derive a contextual subtitle ("On Set — Scenes",
    // "Fittings — Scenes" or just "Scenes" when no lens is locked).
    const ctxSubtitle = lens ? `${LENS_LABELS[lens]} — Scenes` : "Scenes";

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <ProjectHeader projectName={project?.name} subtitle={ctxSubtitle} />
                <main className="flex-1 grid place-items-center text-neutral-500">
                    Loading scenes…
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <ProjectHeader projectName={project?.name} subtitle={ctxSubtitle} />
            <main className="max-w-2xl mx-auto px-5 pt-5 pb-48 w-full">
                <div className="mb-4 flex items-start gap-3">
                    <div
                        data-testid="scenes-hub-identity-icon"
                        className="h-10 w-10 rounded-lg bg-secondary border border-white/10 text-neutral-200 flex items-center justify-center shrink-0"
                    >
                        <Layers size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                            {project?.name || "Project"}
                        </div>
                        <h1
                            data-testid="scenes-hub-title"
                            className="font-display text-4xl font-black text-white mt-1"
                        >
                            Scenes
                        </h1>
                        {isTvMode ? (
                            <p className="text-sm text-neutral-300 mt-1">
                                Grouped by episode — tap an episode to expand.
                            </p>
                        ) : null}
                    </div>
                </div>

                <SegmentedControl
                    testid="scenes-hub-lens-toggle"
                    ariaLabel="Scenes lens"
                    value={lens}
                    onChange={switchLens}
                    options={[
                        { value: "onset", label: LENS_LABELS.onset, testid: "scenes-hub-lens-onset" },
                        { value: "prep", label: LENS_LABELS.prep, testid: "scenes-hub-lens-prep" },
                    ]}
                    className="mb-3"
                />

                <button
                    type="button"
                    data-testid="scenes-hub-open-full"
                    onClick={openFullMode}
                    className="tap w-full rounded-xl border border-white/10 bg-card p-3 mb-5 inline-flex items-center justify-between gap-3 hover:bg-white/5"
                >
                    <span className="inline-flex items-center gap-2 text-sm text-neutral-200">
                        {lens === "prep" ? <Wand2 size={16} /> : <Clapperboard size={16} />}
                        Open {LENS_LABELS[lens]} mode
                    </span>
                    <ArrowRight size={16} className="text-neutral-500" />
                </button>

                {/* Iter-158.13.2.20f.42-final — Bulk Edit Scenes entry.
                    Available in BOTH Fittings/Prep and On Set lenses
                    (Bulk edit is a whole-script editing workspace, not
                    a lens-scoped affordance). Gated by `scenes_edit`
                    via /permissions/me — same pattern as
                    CastHub → Bulk edit cast. */}
                {canBulkEditScenes ? (
                    <Link
                        data-testid="scenes-hub-bulk-edit-link"
                        to={`${routeBase}/${projectId}/scenes/bulk`}
                        className="tap w-full rounded-xl border border-primary/40 bg-primary/10 hover:bg-primary/20 p-3 mb-5 inline-flex items-center justify-between gap-3"
                    >
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                            <Pencil size={14} />
                            Bulk edit scenes
                        </span>
                        <ArrowRight size={16} className="text-primary/70" />
                    </Link>
                ) : null}

                {error ? (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 text-red-200 text-sm p-3 mb-4">
                        {error}
                    </div>
                ) : null}

                {/* iter-64 #5 — Search is no longer sticky in ScenesHub:
                    user feedback was that the floating bar felt awkward
                    over the content, and ScenesHub already keeps the
                    ProjectHeader sticky at top-0 for navigation. The
                    search now scrolls naturally with the list while
                    matching the same visual shell as OnSetMode/PrepMode
                    for layout consistency.
                    iter-69 PART 1 — drop the `-mx-5 px-5` full-bleed
                    wrapper so the search bar's horizontal extent matches
                    the cards/lists below (no more edge-to-edge feel). */}
                <div className="mb-5">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search
                                size={18}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                            />
                            <Input
                                ref={searchRef}
                                data-testid="scenes-hub-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={
                                    isTvMode
                                        ? "Search · try 1.4 for Ep1 Sc4"
                                        : "Search scenes"
                                }
                                className="h-12 pl-11 text-base bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                        </div>
                        {canAddScenes ? (
                            <Button
                                data-testid="scenes-hub-add-scene-btn"
                                type="button"
                                onClick={() => setAddOpen(true)}
                                className="h-12 w-12 px-0 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shrink-0"
                                title="Add scene"
                                aria-label="Add scene"
                            >
                                <Plus size={20} />
                            </Button>
                        ) : null}
                    </div>
                    {/* Iter-118.13 PART 1 — Look filter rail. Renders only
                        when at least one Look exists in the project so
                        we don't add empty UI weight to fresh projects. */}
                    {projectLooks.length > 0 ? (
                        <div className="mt-3" data-testid="scenes-hub-look-filter">
                            <LookFilter
                                looks={projectLooks}
                                value={lookFilter}
                                onChange={setLookFilter}
                                testidPrefix="scenes-hub-look-filter"
                            />
                            {lookFilter ? (
                                <p
                                    data-testid="scenes-hub-look-active-hint"
                                    className="mt-1.5 text-[11px] text-neutral-500 leading-relaxed"
                                >
                                    {lookLoading
                                        ? "Finding scenes…"
                                        : lookSceneIds && lookSceneIds.size > 0
                                            ? `Showing scenes that contain the “${lookFilter}” look (${lookSceneIds.size} scene${lookSceneIds.size === 1 ? "" : "s"}).`
                                            : `No scenes contain the “${lookFilter}” look yet.`}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                {scenes.length === 0 ? (
                    <EmptyScriptOnboarding
                        projectId={projectId}
                        variant="scenes"
                        role={project?.role}
                        canManageScript={project?.can_manage_script}
                        className="mt-3"
                    />
                ) : filteredGroups.length === 0 ? (
                    <div
                        data-testid="scenes-hub-no-match"
                        className="rounded-xl border border-dashed border-white/10 bg-card p-6 text-center text-neutral-300 text-sm mt-3"
                    >
                        No scenes match &ldquo;{q}&rdquo;.
                    </div>
                ) : (
                    <ul data-testid="scenes-hub-list" className="space-y-3 mt-3">
                        {filteredGroups.map((g) => {
                            // Iter-102 PART 1 — default to collapsed
                            // until the user taps. Previously this read
                            // `?? false` which flashed the group open
                            // for one render before the useEffect
                            // populated the map.
                            // Iter-149.1 (P0) — for film projects there
                            // is NO episode header (`showHeader=false`),
                            // so a collapsed-by-default state would
                            // leave the user with an invisible scene
                            // list and no way to expand it. Force
                            // expanded whenever there's no header to
                            // tap.
                            const showHeader = isTvMode || g.key !== NO_EPISODE;
                            const isCollapsed = showHeader
                                ? (collapsed[g.key] ?? true)
                                : false;
                            const epLabel =
                                g.key === NO_EPISODE
                                    ? EPISODE_LABEL_FILM
                                    : EPISODE_LABEL_TV(g.key);
                            // Iter-157P.16 — show active count in the
                            // header (so "EP 2 · 49 scenes" reflects
                            // working scenes, not the bloated total
                            // that included omitted rows pre-P.16).
                            const headerActiveCount = g.scenes.filter((s) => !s.omitted).length;
                            const headerOmittedCount = g.scenes.length - headerActiveCount;
                            return (
                                <li key={g.key}>
                                    {showHeader ? (
                                        <button
                                            type="button"
                                            data-testid={`scenes-episode-toggle-${g.key}`}
                                            onClick={() => toggleEp(g.key)}
                                            className="tap w-full flex items-center gap-2 px-3 py-2.5 rounded-md bg-secondary/60 border border-white/10 hover:bg-white/5"
                                            aria-expanded={!isCollapsed}
                                        >
                                            <ChevronDown
                                                size={14}
                                                className={`text-neutral-400 transition-transform ${
                                                    isCollapsed ? "-rotate-90" : ""
                                                }`}
                                            />
                                            <span className="font-display font-bold text-white text-sm">
                                                {epLabel}
                                            </span>
                                            <span
                                                data-testid={`scenes-episode-counts-${g.key}`}
                                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 ml-auto"
                                            >
                                                {headerActiveCount} scene
                                                {headerActiveCount === 1 ? "" : "s"}
                                                {headerOmittedCount > 0 ? (
                                                    <span className="ml-2 text-neutral-600">
                                                        · {headerOmittedCount} omitted
                                                    </span>
                                                ) : null}
                                            </span>
                                        </button>
                                    ) : null}
                                    {!isCollapsed ? (
                                        <ul
                                            data-testid={`scenes-episode-list-${g.key}`}
                                            className={`space-y-2 ${
                                                showHeader ? "mt-2 ml-3" : ""
                                            }`}
                                        >
                                            {g.scenes.map((s) => {
                                                // Iter-158.13.2.20f.2 #12 — when the
                                                // group header already renders the
                                                // episode label (`showHeader && TV
                                                // mode`), the scene card drops the
                                                // redundant `ep.` prefix and shows
                                                // just the scene number. Film rows
                                                // (no header) and rows missing
                                                // episode metadata render byte-
                                                // identical to before.
                                                const combined = showHeader && s.episode && s.scene_number
                                                    ? String(s.scene_number)
                                                    : (s.episode && s.scene_number
                                                        ? `${s.episode}.${s.scene_number}`
                                                        : s.scene_number || "—");
                                                // Iter-157P.18.2 — list views answer ONE
                                                // question: "is this scene omitted?". Amber
                                                // = omitted, regardless of cause (script /
                                                // revision / later pages). The detailed
                                                // explanation lives on Scene Detail.
                                                const byRevision = Boolean(s.omitted_by_revision_id);
                                                const omittedWrapperCls =
                                                    "border-amber-500/50 bg-amber-500/10 border-dashed";
                                                const omittedNumCls = "text-amber-200 line-through";
                                                const omittedHeadingCls = "text-amber-50/95";
                                                const omittedMetaCls = "text-amber-100/80";
                                                const omittedBadgeCls =
                                                    "text-[10px] uppercase tracking-[0.2em] text-amber-50 bg-amber-500/30 border border-amber-400/60 rounded-full px-2 py-0.5 font-semibold";
                                                return (
                                                    <li key={s.id}>
                                                        <Link
                                                            data-testid={`scenes-hub-scene-${s.id}`}
                                                            data-shot={s.has_onset_entries ? "true" : "false"}
                                                            data-omitted={s.omitted ? "true" : "false"}
                                                            data-omission-cause={
                                                                s.omitted
                                                                    ? (byRevision ? "revision" : "script")
                                                                    : undefined
                                                            }
                                                            to={`${routeBase}/${projectId}/scenes/${s.id}`}
                                                            state={{
                                                                returnTo: {
                                                                    path: `${routeBase}/${projectId}/scenes`,
                                                                    label: "Back to scenes",
                                                                    // Iter-155B.1 — preserve the
                                                                    // episode the user was browsing
                                                                    // so Back doesn't reset the
                                                                    // ScenesHub to "all collapsed".
                                                                    expandEpisode: g.key,
                                                                    scrollToSceneId: s.id,
                                                                },
                                                            }}
                                                            className={`tap flex items-center gap-3 rounded-xl border p-4 ${
                                                                s.omitted
                                                                    ? omittedWrapperCls
                                                                    : "border-white/10 bg-card hover:bg-white/5"
                                                            } ${
                                                                s.has_onset_entries && !s.omitted
                                                                    ? "opacity-50 hover:opacity-90"
                                                                    : ""
                                                            }`}
                                                        >
                                                            <div
                                                                data-testid={`scenes-hub-scene-${s.id}-num`}
                                                                className={`shrink-0 mono font-bold leading-none text-center whitespace-nowrap tabular-nums px-1 ${
                                                                    s.omitted ? omittedNumCls : "text-primary"
                                                                } ${
                                                                    s.episode
                                                                        ? "min-w-[60px] text-base"
                                                                        : "w-14 text-2xl"
                                                                }`}
                                                            >
                                                                {combined}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span
                                                                        className={`font-medium truncate ${
                                                                            s.omitted ? omittedHeadingCls : "text-white"
                                                                        }`}
                                                                    >
                                                                        {s.heading || "(No heading)"}
                                                                    </span>
                                                                    {s.has_onset_entries && !s.omitted ? (
                                                                        <span
                                                                            data-testid={`scenes-hub-shot-badge-${s.id}`}
                                                                            className="text-[9px] uppercase tracking-[0.2em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                                                        >
                                                                            Shot
                                                                        </span>
                                                                    ) : null}
                                                                    {s.omitted ? (
                                                                        <span
                                                                            data-testid={`scenes-hub-omitted-badge-${s.id}`}
                                                                            data-omission-cause={byRevision ? "revision" : "script"}
                                                                            className={omittedBadgeCls}
                                                                            title={
                                                                                byRevision
                                                                                    ? "Omitted by approved script revision. Continuity preserved. Tap the scene for full details."
                                                                                    : "Marked omitted in the original script. Continuity preserved. Tap the scene for full details."
                                                                            }
                                                                        >
                                                                            Omitted
                                                                        </span>
                                                                    ) : null}
                                                                    {s.added_by_revision_id && !s.omitted ? (
                                                                        <span
                                                                            data-testid={`scenes-hub-inserted-badge-${s.id}`}
                                                                            className="text-[10px] uppercase tracking-[0.2em] text-emerald-100 bg-emerald-500/25 border border-emerald-400/50 rounded-full px-2 py-0.5 font-semibold"
                                                                            title="Scene inserted by approved script revision."
                                                                        >
                                                                            Inserted
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                                <div className={`text-xs mt-0.5 ${s.omitted ? omittedMetaCls : "text-neutral-400"}`}>
                                                                    {[s.int_ext, s.time_of_day]
                                                                        .filter(Boolean)
                                                                        .join(" · ") || "—"}
                                                                    {(sceneCharMap[s.id] || []).length
                                                                        ? ` · ${sceneCharMap[s.id].length} chars`
                                                                        : null}
                                                                </div>
                                                            </div>
                                                            <ArrowRight
                                                                size={14}
                                                                className="text-neutral-500 shrink-0"
                                                            />
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </main>

            <AddSceneDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                projectId={projectId}
                existingScenes={scenes}
                onCreated={(created) => {
                    setScenes((prev) => [...prev, created]);
                    setAddOpen(false);
                }}
            />
        </div>
    );
}
